import { BaseGameRecorder, generateId } from '@bn-games/history-core';
import type { Tile, Turn } from '../game/types.js';
import type { DiceRollProof } from '../network/protocol.js';
import type { DominoRuleSet } from '../game/variants/types.js';
import { ActiveGameStore } from './ActiveGameStore.js';
import type {
  ActiveSession,
  GameRecord,
  RecordedMove,
  RecordedSeed,
} from './types.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';

export type SeedRuleFields = Pick<
  RecordedSeed,
  'pointGoal' | 'mugginsStrict' | 'opening13' | 'baldFinish'
>;

/**
 * The rule values a seed stores, read from the rule set the game actually
 * runs — not from Settings, whose fives `pointGoal` would be misleading on a
 * Козёл record (fixed 101).
 */
export function seedRuleFields(ruleSet: DominoRuleSet): SeedRuleFields {
  return {
    pointGoal: ruleSet.pointGoal,
    mugginsStrict: ruleSet.mugginsStrict,
    opening13: ruleSet.opening13,
    baldFinish: ruleSet.baldFinish,
  };
}

/**
 * Owns the in-flight `ActiveSession` for the current domino match. The base
 * class handles session id/storage write-through; this subclass owns the
 * per-game `RecordedMove` union — appending one entry per engine reducer call
 * (play / draw / pass / claim or expire muggins / start next hand).
 */
export class GameRecorder extends BaseGameRecorder<ActiveSession> {
  constructor() {
    super(ActiveGameStore);
  }

  start(seed: RecordedSeed): void {
    this.setSession({
      id: generateId(),
      version: HISTORY_SCHEMA_VERSION,
      seed,
      moves: [],
      updatedAt: Date.now(),
    });
  }

  recordPlay(seat: number, tile: Tile, armIndex: number, turn: Turn, points: number): void {
    this.append({ kind: 'play', seat, tile, armIndex, turn, points });
  }

  recordDraw(seat: number): void {
    this.append({ kind: 'draw', seat });
  }

  recordPass(seat: number): void {
    this.append({ kind: 'pass', seat });
  }

  recordClaimMuggins(seat: number, points: number): void {
    this.append({ kind: 'claim-muggins', seat, points });
  }

  recordExpireMuggins(): void {
    this.append({ kind: 'expire-muggins' });
  }

  recordNextHand(seed: number, proof?: DiceRollProof): void {
    this.append(
      proof !== undefined
        ? { kind: 'next-hand', seed, proof }
        : { kind: 'next-hand', seed },
    );
  }

  /** Online STATE_SYNC arrived with a wholesale snapshot — the local move
   * trace is now stale. Reset moves but keep id + seed so the record still
   * points to the same match. */
  resetTrace(): void {
    this.updateSession((s) => ({
      ...s,
      moves: [],
      updatedAt: Date.now(),
    }));
  }

  finish(
    result: 'win' | 'draw' | 'abandoned',
    winnerSeats: readonly number[],
    finalScores: readonly number[],
  ): GameRecord {
    if (!this.session) {
      throw new Error('GameRecorder.finish called with no active session');
    }
    return {
      id: this.session.id,
      version: HISTORY_SCHEMA_VERSION,
      createdAt: this.session.seed.createdAt,
      finishedAt: Date.now(),
      mode: this.session.seed.mode,
      variant: this.session.seed.variant,
      playerCount: this.session.seed.playerCount,
      finalScores,
      winnerSeats,
      result,
      seed: this.session.seed,
      moves: this.session.moves,
    };
  }

  private append(entry: RecordedMove): void {
    this.updateSession((s) => ({
      ...s,
      moves: [...s.moves, entry],
      updatedAt: Date.now(),
    }));
  }
}
